import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  BillEvent,
  BillEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/finance/v1/bill_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { BillCreatedDetail } from './BillCreatedDetail';
import { BillDeletedDetail } from './BillDeletedDetail';
import { BillPaidDetail } from './BillPaidDetail';
import { BillUpdatedDetail } from './BillUpdatedDetail';
import { BillVoidedDetail } from './BillVoidedDetail';

import { useTranslations } from '@/finance/i18n/useTranslations';

type BillEventDetailProps = {
  payload: string;
};

export const BillEventDetail = ({ payload }: BillEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): BillEvent | null => {
    try {
      return fromJson(BillEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <BillCreatedDetail event={inner.value} />;
      case 'updated':
        return <BillUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <BillDeletedDetail event={inner.value} />;
      case 'voided':
        return <BillVoidedDetail event={inner.value} />;
      case 'paid':
        return <BillPaidDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.billCreated;
      case 'updated':
        return t.logs.events.billUpdated;
      case 'deleted':
        return t.logs.events.billDeleted;
      case 'voided':
        return t.logs.events.billVoided;
      case 'paid':
        return t.logs.events.billPaid;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@BillEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
